import { colors } from '@/components/ECharts/colors';
import { CompetitorsMediaInvestmentDataKeys } from '@/dashboards/CompetitorsMediaInvestment/api';
import {
    FilterTypes,
    FormattedCompetitorsMediaInvestmentData,
} from '@/dashboards/CompetitorsMediaInvestment/types';
import { RowValueType } from '@/dashboards/ModelContributions/types';
import { memoize } from 'lodash';
import moment from 'moment/moment';

function getColor(index: number) {
    return colors[index % colors.length]!;
}

export const groupCompetitorsMediaInvestmentData = memoize(
    (
        rows: RowValueType,
        fieldKeys: CompetitorsMediaInvestmentDataKeys,
        {
            formatDate,
            startDate,
            endDate,
        }: {
            formatDate: ((date: string) => string) | undefined;
            startDate: string | undefined;
            endDate: string | undefined;
            cacheKey: string;
        },
    ): FormattedCompetitorsMediaInvestmentData => {
        let categoryColorIndex = 0;

        const categoryData: FormattedCompetitorsMediaInvestmentData['categoryData'] =
            [];
        const variableData: FormattedCompetitorsMediaInvestmentData['variableData'] =
            [];
        const dates: Set<string> = new Set();

        const usedCategoryVariableCombinations = new Set<string>();

        const startDateUtc = moment.utc(startDate, 'DD-MM-YYYY');
        const endDateUtc = moment.utc(endDate, 'DD-MM-YYYY');

        const initialData = {
            investmentDataByVariable: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            investmentDataByCategory: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            totalInvestment: 0,
            totalInvestmentByCategory: new Map<string, number>(),
            totalInvestmentByVariable: new Map<string, number>(),
            variableToCategory: new Map<string, string>(),
            variableData: [],
            categoryData: [],
            competitorFilters: [] as FilterTypes[],
        };

        const data = rows.reduce((acc, item, currentIndex) => {
            const variable = item[fieldKeys.variable]!.value.formatted;
            const category = item[fieldKeys.category]!.value.formatted;
            const dateString = item[fieldKeys.date]!.value.formatted;

            const dateStringUtc = moment(dateString, 'DD-MM-YYYY');

            if (
                startDate &&
                !dateStringUtc.isSame(startDateUtc, 'day') &&
                dateStringUtc.isBefore(startDateUtc)
            ) {
                return acc;
            }

            if (
                endDate &&
                !dateStringUtc.isSame(endDateUtc, 'day') &&
                dateStringUtc.isAfter(endDateUtc)
            ) {
                return acc;
            }

            const date = formatDate ? formatDate(dateString) : dateString;

            dates.add(date);

            const investment = isNaN(
                Number(item[fieldKeys.investment]!.value.raw),
            )
                ? 0
                : Number(item[fieldKeys.investment]!.value.raw);

            if (!acc.investmentDataByVariable.has(variable)) {
                const color = getColor(currentIndex);
                variableData.push({
                    variable: item[fieldKeys.variable]!.value.formatted,
                    category: item[fieldKeys.category]!.value.formatted,
                    color,
                });
                acc.investmentDataByVariable.set(variable, {
                    data: {},
                    color,
                });
                acc.competitorFilters.push({
                    name: variable,
                    label: variable,
                    category: category,
                    selected: true,
                });
            }

            if (!acc.investmentDataByCategory.has(category)) {
                const color = getColor(categoryColorIndex);
                categoryData.push({
                    category: item[fieldKeys.category]!.value.formatted,
                    color,
                });
                acc.investmentDataByCategory.set(category, {
                    data: {},
                    color,
                });
                acc.totalInvestmentByCategory.set(category, 0);
                categoryColorIndex++;
            }

            acc.investmentDataByVariable.get(variable)!.data[date] =
                (acc.investmentDataByVariable.get(variable)!.data[date] ?? 0) +
                    investment || investment;

            acc.investmentDataByCategory.get(category)!.data[date] =
                (acc.investmentDataByCategory.get(category)!.data[date] ?? 0) +
                    investment || investment;

            acc.totalInvestment += investment;
            acc.totalInvestmentByCategory.set(
                category,
                acc.totalInvestmentByCategory.get(category)! + investment,
            );
            acc.totalInvestmentByVariable.set(
                variable,
                (acc.totalInvestmentByVariable.get(variable) ?? 0) + investment,
            );

            acc.variableToCategory.set(variable, category);

            if (
                !usedCategoryVariableCombinations.has(`${category}-${variable}`)
            ) {
                usedCategoryVariableCombinations.add(`${category}-${variable}`);
            }

            return acc;
        }, initialData);

        return Object.assign(data, {
            variableData,
            categoryData,
            dates: Array.from(dates),
        });
    },
    (_0, _1, options) => JSON.stringify({ options }),
);
